import React from 'react';
import classnames, { Argument } from 'classnames';

import { BiomeIcon, IconName } from '@codametrix/verdigris';
import {
  commonActions,
  commonEnums,
  objectUtils,
  AmplifyCore,
  appServiceLines
} from '@codametrix/ui-common';
import {
  FieldSetWrapper,
  FieldWrapper,
  Form,
  FormButtonContainer,
  Modal,
  SelectList,
  Icon
} from '@codametrix/ui-components';

import { Lookup } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import { Conditional } from '@codametrix/ui-components';

export type InvoiceEditorProps = Accelerate.InvoiceMetadataState &
  Accelerate.InvoiceMetadataMethods;
type InvoiceEditorState = {
  departmentSearchText: string;
  requiredFieldsValid?: { [key: string]: boolean };
  disableSubmit?: boolean;
};

const { invoiceUtils } = AmplifyCore;
const { InvoiceEditor: InvoiceEditorActions } = commonActions;
const { RequiredField } = commonEnums;
const { compareObjectProperties, isFieldValid } = invoiceUtils;

export const FieldDefinitions = [
  { label: 'Last Name', value: 'lastName', key: 'lastName' },
  { label: 'ID', value: 'id', key: 'id', disabled: false }
];

export const FieldIPCDefinitions = [
  { label: 'Name', value: 'name', key: 'name' }
];

const shouldShowResults = (lookupState: CMxCommonApp.CodeLookupState) => {
  const { active, searching, codeQuery } = lookupState;
  if (!active) {
    return false;
  }
  if (searching) {
    return true;
  }
  // one character names are displayable.
  // they may not be totally valid, but there are
  // instances in our database where they exist.
  // https://drive.google.com/file/d/1Eur55UvRKHmo2aYA7Q_lBx9EGSbU4kSH/view?usp=sharing
  return (codeQuery?.text ?? '').length >= 1;
};

const resultFormatter = (activeIdx: number, provider: any, idx: number) => {
  let identifier = provider.identifiers.find((id) => id.primary);
  let selectable = true;

  // We can't rely on there being an identifier from the API.
  // There may not be a primary, or there might not be an identifier
  // at all.  In the case of there being no identifier for the
  // provider, the result will be unselectable by the user.
  if (!identifier) {
    if (!provider.identifiers.length) {
      selectable = false;

      identifier = {
        idNumber: 'N/A',
        idTypeCode: 'N/A'
      };
    } else {
      // if we don't have a primary identifier, just choose a random
      // identifier at the moment.
      identifier = provider.identifiers[0];
    }
  }

  const styleClasses: Argument[] = [
    'search-result',
    {
      'search-result-submittable': activeIdx === idx,
      'result-unselectable': !selectable
    }
  ];

  return (
    <div
      key={idx}
      data-idx={idx}
      className={classnames(styleClasses)}
      draggable={true}>
      <div className="col-label search-label"></div>
      <div className="col-code-description">
        {provider.displayName}
        <div className="id-display">
          ID: {identifier.idNumber} (src: {identifier.idTypeCode})
        </div>
      </div>
    </div>
  );
};

const resultFormatterDept = (
  activeIdx: number,
  department: any,
  idx: number
) => {
  const styleClasses: Argument[] = [
    'search-result',
    {
      'search-result-submittable': activeIdx === idx
    }
  ];

  return (
    <div
      key={idx}
      data-idx={idx}
      className={classnames(styleClasses)}
      draggable={true}>
      <div className="col-code-description">{department.description}</div>
    </div>
  );
};

export const resultFormatterV2 = (
  activeIdx: number,
  provider: any,
  idx: number
) => {
  let identifier = provider.identifiers.find((id) => id?.primary);
  const isProviderBillable = provider?.specialties?.some(
    (specialty) => specialty?.billable
  );
  let selectable = isProviderBillable;

  // We can't rely on there being an identifier from the API.
  // There may not be a primary, or there might not be an identifier
  // at all.  In the case of there being no identifier for the
  // provider, the result will be unselectable by the user.
  if (!identifier) {
    if (!provider.identifiers.length) {
      selectable = false;

      identifier = {
        idNumber: 'N/A',
        idTypeCode: 'N/A'
      };
    } else {
      // if we don't have a primary identifier, just choose a random
      // identifier at the moment.
      identifier = provider?.identifiers[0];
    }
  }

  const isNotSectable =
    (provider?.isBillableSelectable && !selectable) || !provider?.isSelectable;
  const styleClasses: Argument[] = [
    'search-result',
    'ipc-provider-search',
    {
      'search-result-submittable': activeIdx === idx,
      'result-unselectable': isNotSectable
    }
  ];

  const specialtiesByLoc = objectUtils.groupBy(
    provider?.specialties ?? [],
    'location'
  );
  const specialtiesView = Object.entries(specialtiesByLoc)?.map(
    ([location, locationGroup]) => {
      const specialtiesByRole = objectUtils.groupBy(
        locationGroup ?? [],
        'name'
      );
      return Object.entries(specialtiesByRole)?.map(([name, specialties]: [string, XCaliberAPI.Specialty[]]) => {
        const isRoleBillable = specialties?.some(
          (specialty: XCaliberAPI.Specialty) => specialty?.billable
        );
        const roleStyles = [
          'provider-specialties',
          {
            'billable-role': isRoleBillable,
            'non-billable-role': !isRoleBillable
          }
        ];
        return (
          <div key={location}>
            {location && location !== 'null' && (
              <div className="provider-location">{location}</div>
            )}
            <div className={classnames(roleStyles)}>
              <div className="provider-role">{name}</div>
              {specialties?.map((specialty: XCaliberAPI.Specialty) => {
                return (
                  <div
                    key={specialty?.specialty}
                    className="provider-specialty">
                    <div className="specialty-name">{specialty?.specialty}</div>
                    {specialty?.specialty_flag && (
                      <div className="specialty-flag">
                        {specialty?.specialty_flag}
                      </div>
                    )}
                    {specialty?.billable && (
                      <CMXIcon
                        svgTitleText="Billable"
                        svgIconName="BiomeCoding"
                        svgIconClass="billing-icon"
                        svgIconSize="16"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      });
    }
  );

  return (
    <div
      key={idx}
      data-idx={idx}
      className={classnames(styleClasses)}
      draggable={true}>
      <div className="col-code-description">
        <div className="provider-header">
          <span className="encounter-bg">
            <CMXIcon
              svgIconName="BiomeEncounterCircle"
              svgIconClass="encounter-icon"
              svgIconSize="24"
            />
          </span>
          <span className="provider-name-section">
            <span className="provider-name">
              {provider?.displayName}
              {provider?.degree && `,`}
            </span>
            <span className="provider-degree">{provider?.degree}</span>
          </span>
          <span className="provider-id">({identifier.idNumber})</span>
          <span className="provider-service-idtypecode">
            {provider.idTypeCode}
          </span>
        </div>
        <Conditional every={[isNotSectable]}>
          <span className="provider-specialties not-eligible">
            Not Eligible
          </span>
        </Conditional>
        <Conditional every={[!isNotSectable]}>
          <>{specialtiesView}</>
        </Conditional>
      </div>
    </div>
  );
};
const getSelectionData = (
  row: HTMLDivElement,
  selectElement: HTMLSelectElement,
  searchResults: any
) => {
  const item = row?.attributes.getNamedItem('data-idx');
  if (!item) return;
  return searchResults[item.value];
};

export const DEFAULT_ARGS = {
  getSelectionData,
  shouldShowResults,
  resultFormatter,
  autoBlur: true,
  debounceTimeout: 500,
  minLength: 0,
  billingProviderSearchResults: [],
  referringProviderSearchResults: [],
  performingProviderSearchResults: [],
  FieldDefinitions
};

export const DEFAULT_ARGS_V2 = {
  ...DEFAULT_ARGS,
  resultFormatter: resultFormatterV2,
  FieldDefinitions: FieldIPCDefinitions
};

type DepartmentOption = {
  description: string;
  code: string;
};

const departmentSearch = (
  department: DepartmentOption,
  departmentSearchText: string
) => {
  // allow show all when search text is just three numbers or lower
  if (departmentSearchText.length <= 3) {
    return true;
  }

  // if there is no description, search by code
  if (!department.description && department.code) {
    return (
      department.code
        .toLowerCase()
        .startsWith(departmentSearchText.toLowerCase()) ||
      department.code.toLowerCase().includes(departmentSearchText.toLowerCase())
    );
  }

  // by default search by description
  return (
    department.description
      .toLowerCase()
      .startsWith(departmentSearchText.toLowerCase()) ||
    department.description
      .toLowerCase()
      .includes(departmentSearchText.toLowerCase())
  );
};

class InvoiceEditor extends React.Component<
  InvoiceEditorProps,
  InvoiceEditorState
> {
  static defaultProps = {
    billingProviderSearchResults: [],
    referringProviderSearchResults: [],
    performingProviderSearchResults: [],
    billingAreaOptions: [],
    providerRoleTypeConfig: [],
    useProviderDirectoryLocations: {
      on: false
    },
    onInvoiceSave: (savableInvoice: Accelerate.SavableInvoice) => {},
    onProviderRemove: (invoice: CMxCommonApp.CodableInvoice) => {},
    onProviderAdd: (provider: any) => {},
    onInvoiceCancel: () => {},
    onInvoiceCreate: (savableInvoice: Accelerate.SavableInvoice) => {},
    onPerformerAdditionRemove: (invoice: CMxCommonApp.CodableInvoice) => {},
    onReferringPerformerAdditionRemove: (
      invoice: CMxCommonApp.CodableInvoice
    ) => {},
    onPerformerAdditionAdd: (provider: any) => {},
    onReferrerPerformerAdditionAdd: (provider: any) => {},
    onPerformerAdditionSearch: (query) => {},
    onPerformDateChange: (newDate) => {}
  };

  constructor(props: InvoiceEditorProps) {
    super(props);
    this.state = {
      requiredFieldsValid: {},
      departmentSearchText: ''
    };
    this.handleProviderRemove = this.handleProviderRemove.bind(this);
    this.handleProviderSearch = this.handleProviderSearch.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handlePerformerRemove = this.handlePerformerRemove.bind(this);
    this.handleReferringPerformerRemove =
      this.handleReferringPerformerRemove.bind(this);
    this.handlePerformerSearch = this.handlePerformerSearch.bind(this);
    this.handleReferrerSearch = this.handleReferrerSearch.bind(this);
    this.onPerformDateChange = this.onPerformDateChange.bind(this);
    this.departmentChange = this.departmentChange.bind(this);
    this.departmentRemove = this.departmentRemove.bind(this);
    this.feeScheduleChange = this.feeScheduleChange.bind(this);
    this.handleDepartmentSearch = this.handleDepartmentSearch.bind(this);
    this.handleFormValidation = this.handleFormValidation.bind(this);
    this.resetRequiredFields = this.resetRequiredFields.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.checkRequiredFieldValid = this.checkRequiredFieldValid.bind(this);
    this.handleOnPerformerAdditionAdd =
      this.handleOnPerformerAdditionAdd.bind(this);
    this.handleOnReferrerPerformerAdditionAdd =
      this.handleOnReferrerPerformerAdditionAdd.bind(this);
    this.handleOnProviderAdd = this.handleOnProviderAdd.bind(this);

    this.state = {
      departmentSearchText: ''
    };
  }

  componentDidUpdate(
    prevProps: Readonly<InvoiceEditorProps>,
    prevState: Readonly<InvoiceEditorState>,
    snapshot?: any
  ): void {
    const comparePropsList = [
      'billing_area',
      'department.code',
      'billing_provider_number.number',
      'performing_provider_number.number',
      'referring_provider_number.number',
      'date_of_service'
    ];
    if (
      !compareObjectProperties(
        prevProps?.invoice,
        this.props?.invoice,
        comparePropsList
      )
    ) {
      this.handleFormValidation();
    }
  }

  resetRequiredFields() {
    this.setState({
      ...this.state,
      requiredFieldsValid: {}
    });
  }
  handleCancel(ev?: React.SyntheticEvent) {
    ev?.preventDefault();
    this.resetRequiredFields();
    this.props.onInvoiceCancel(ev);
  }
  checkRequiredFieldValid(field: string) {
    return this.state.requiredFieldsValid?.[field] ?? true;
  }
  handleFormValidation() {
    const requiredFields = this.props.invoiceOptions?.required_fields;
    const invoice = this.props.invoice;
    const isBillingProviderValid = isFieldValid(
      requiredFields,
      RequiredField.BILLING_PROVIDER,
      invoice?.billing_provider_number?.number
    );
    const isReferringProviderValid = isFieldValid(
      requiredFields,
      RequiredField.REFERRING_PROVIDER,
      invoice?.referring_provider_number?.number
    );
    const isPerformingProviderValid = isFieldValid(
      requiredFields,
      RequiredField.PERFORMING_PROVIDER,
      invoice?.performing_provider_number?.number
    );
    const isDepartmentValid = isFieldValid(
      requiredFields,
      RequiredField.DEPARTMENT,
      invoice?.department?.code
    );
    const isBillingAreaValid = isFieldValid(
      requiredFields,
      RequiredField.BILLING_AREA,
      invoice?.fee_schedule
    );
    const isDateOfServiceValid = isFieldValid(
      requiredFields,
      RequiredField.DATE_OF_SERVICE,
      invoice?.date_of_service
    );

    const requiredFieldsValid = {
      [RequiredField.BILLING_AREA]: isBillingAreaValid,
      [RequiredField.DEPARTMENT]: isDepartmentValid,
      [RequiredField.BILLING_PROVIDER]: isBillingProviderValid,
      [RequiredField.PERFORMING_PROVIDER]: isPerformingProviderValid,
      [RequiredField.REFERRING_PROVIDER]: isReferringProviderValid,
      [RequiredField.DATE_OF_SERVICE]: isDateOfServiceValid
    };

    const isValid = Object.values(requiredFieldsValid).every((val) => val);
    this.setState({
      ...this.state,
      requiredFieldsValid: requiredFieldsValid,
      disableSubmit: !isValid
    });

    return isValid;
  }

  handleFormSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    let { invoice, processInstance } = this.props;
    this.resetRequiredFields();
    if (this.handleFormValidation()) {
      this.resetRequiredFields();
      if (this.props.displayMode === commonEnums.InvoiceEditorMode.EDITOR) {
        this.props.onInvoiceSave({ invoice, processInstance });
      } else if (
        this.props.displayMode === commonEnums.InvoiceEditorMode.ADDITION
      ) {
        this.props.onInvoiceCreate({ invoice, processInstance });
      }
    }
  }

  handleProviderRemove(ev: React.MouseEvent<HTMLElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    this.props.onProviderRemove(this.props.invoice);
  }

  /**
   * method that handle when a search is done
   * @param codeQuery
   */
  handlePerformerSearch(codeQuery: CMxCommonApp.CodeQuery) {
    const context = {
      ...codeQuery.context,
      searchType: InvoiceEditorActions.PERFORMING_PROVIDER_RESULTS,
      processInstance: this.props.processInstance,
      providerType: commonEnums.ProviderType.PERFORMING_PROVIDER,
      invoice: this.props.invoice,
      shouldFilterActive: true,
      shouldGreyOutExternalProviders: true,
      providerRoleTypeConfig: this.props.providerRoleTypeConfig
    };
    this.props.onPerformerAdditionSearch({
      ...codeQuery,
      context
    });
  }

  /**
   * method that handle when a search is done
   * @param codeQuery
   */
  handleDepartmentSearch(codeQuery: CMxCommonApp.CodeQuery) {
    this.setState({ departmentSearchText: codeQuery.text });
  }

  handleReferrerSearch(codeQuery: CMxCommonApp.CodeQuery) {
    const context = {
      ...codeQuery.context,
      searchType: InvoiceEditorActions.PERFORMING_PROVIDER_RESULTS,
      processInstance: this.props.processInstance,
      providerType: commonEnums.ProviderType.REFERRING_PROVIDER,
      invoice: this.props.invoice,
      shouldFilterActive: true,
      shouldFilterOutExternalProviders: false
    };
    this.props.onPerformerAdditionSearch({
      ...codeQuery,
      context
    });
  }

  handleProviderSearch(codeQuery: CMxCommonApp.CodeQuery) {
    const context = {
      ...codeQuery.context,
      searchType: InvoiceEditorActions.BILLING_PROVIDER_RESULTS,
      processInstance: this.props.processInstance,
      providerType: commonEnums.ProviderType.BILLING_PROVIDER,
      shouldFilterActive: true,
      invoice: this.props.invoice,
      useProviderDirectoryLocations: this.props.useProviderDirectoryLocations
    };
    this.props.onProviderSearch({
      ...codeQuery,
      context
    });
  }

  /**
   * method that handle when a performer wants to be removed
   * @param ev
   */
  handlePerformerRemove(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    this.props.onPerformerAdditionRemove(this.props.invoice);
  }

  /**
   * method that handle when a performer wants to be removed
   * @param ev
   */
  handleReferringPerformerRemove(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    this.props.onReferringPerformerAdditionRemove(this.props.invoice);
  }

  /**
   * method that hanlde when a date has been changed
   * @param ev
   */
  onPerformDateChange(ev: React.ChangeEvent<HTMLInputElement>) {
    ev.preventDefault();
    this.props.onPerformDateChange(ev.target.value);
  }

  departmentChange(departmentObj: Accelerate.LookupSelectionPayload<any>) {
    this.props.onDepartmentChange(departmentObj.codeInfo.code);
  }

  departmentRemove(ev?: React.MouseEvent<HTMLButtonElement>) {
    ev?.preventDefault();
    ev?.stopPropagation();
    this.props.onRemoveDepartment();
  }

  feeScheduleChange(feeScheduleObj: CustomEvent) {
    this.props.onFeeScheduleChange(feeScheduleObj.detail.value);
  }

  handleOnPerformerAdditionAdd(
    payload: Accelerate.LookupSelectionPayload<any>
  ) {
    const provider = { ...payload.codeInfo, ...payload.context };
    this.props.onPerformerAdditionAdd(provider);
  }

  handleOnReferrerPerformerAdditionAdd(
    payload: Accelerate.LookupSelectionPayload<any>
  ) {
    const provider = { ...payload.codeInfo, ...payload.context };
    this.props.onReferrerPerformerAdditionAdd(provider);
  }

  handleOnProviderAdd(payload: Accelerate.LookupSelectionPayload<any>) {
    const provider = { ...payload.codeInfo, ...payload.context };
    this.props.onProviderAdd(provider);
  }

  render() {
    const { displayMode, billingAreaOptions, invoice, invoiceOptions } =
      this.props;

    if (!displayMode) return <span className="charge-editor-form-empty"></span>;

    const hasBillingProvider = invoice?.billing_provider_name ? true : false;
    const hasPerformer = invoice?.performing_provider_name ? true : false;
    const hasReferringPerformer = invoice?.referring_provider_name
      ? true
      : false;

    const feeSchedules = objectUtils.convertRecordToOptionList(
      invoiceOptions.summary.fee_schedules
    );

    const existingDepartments = new Set<string>(
      invoiceOptions?.summary?.existing_departments?.map((obj) => obj.code)
    );
    const allDepartmentOptions = invoiceUtils.getPreparedDepartmentOptions(
      invoiceUtils.getAllDepartments(invoiceOptions),
      existingDepartments
    );

    const isIPCServiceLine =
      appServiceLines.isIPC(this.props.processInstance?.serviceLine) ?? false;
    const isSurgeryServiceLine =
      appServiceLines.isSurgery(this.props.processInstance?.serviceLine) ??
      false;

    const lookupArgs =
      isIPCServiceLine || isSurgeryServiceLine
        ? { ...DEFAULT_ARGS_V2 }
        : { ...DEFAULT_ARGS };

    return (
      <div className="charge-editor-form">
        <Modal onClose={this.handleCancel}>
          <div className="cmx-form cmx-block-form charge-editor-modal">
            <Form
              formDefinition={{
                fieldGroups: [],
                fields: [],
                buttons: []
              }}>
              <form  className="cmx-form-tag" >
                <FieldSetWrapper legend="Billing Information">
                  <FieldWrapper
                    key="datePerformingInfo"
                    label="Date"
                    type="date-performing-info"
                    required={invoiceOptions?.required_fields?.includes(
                      RequiredField.DATE_OF_SERVICE
                    )}>
                    <input
                      type="date"
                      className={
                        !invoice?.date_of_service ? 'input-invalid' : ''
                      }
                      value={invoice?.date_of_service}
                      onChange={this.onPerformDateChange}
                    />
                    <Conditional
                      every={[
                        this.checkRequiredFieldValid(
                          RequiredField.DATE_OF_SERVICE
                        )
                      ]}>
                      <span className="help-text help-text-select">
                        The date of service
                      </span>
                    </Conditional>
                    <Conditional
                      every={[
                        !this.checkRequiredFieldValid(
                          RequiredField.DATE_OF_SERVICE
                        )
                      ]}>
                      <span className="error-text help-text help-text-select">
                        Date of service is required
                      </span>
                    </Conditional>
                  </FieldWrapper>

                  <FieldWrapper
                    key="billingProvider"
                    label="Provider"
                    type="billing-provider"
                    required={invoiceOptions?.required_fields?.includes(
                      RequiredField.BILLING_PROVIDER
                    )}>
                    {hasBillingProvider ? (
                      <div className="cmx-chip cmx-chip-entity">
                        {invoice.billing_provider_name}
                        <button
                          type="button"
                          autoFocus={true}
                          onClick={this.handleProviderRemove}
                          className="cmx-chip-entity-remove btn-remover"
                          aria-label="Remove provider">
                          <BiomeIcon
                            iconName={IconName.Cancel}
                            iconSize="16"
                            iconClass="cmx-chip-entity-remove__icon"
                          />
                        </button>
                      </div>
                    ) : (
                      <Lookup
                        {...lookupArgs}
                        autoFocus={true}
                        onSearchSelection={this.handleOnProviderAdd}
                        onSearch={this.handleProviderSearch}
                        searchResults={this.props.billingProviderSearchResults}
                        actionPrefix={''}
                        disabled={!invoice?.date_of_service}
                        autoSearch={true}
                        placeholderText={
                          isIPCServiceLine
                            ? 'Search for a provider by name'
                            : 'Search for a provider'
                        }
                        debounceTimeout={300}
                      />
                    )}

                    <Conditional
                      every={[
                        this.checkRequiredFieldValid(
                          RequiredField.BILLING_PROVIDER
                        )
                      ]}>
                      <span className="help-text help-text-select">
                        The provider who is responsible for billing
                      </span>
                    </Conditional>
                    <Conditional
                      every={[
                        !this.checkRequiredFieldValid(
                          RequiredField.BILLING_PROVIDER
                        )
                      ]}>
                      <span className="error-text help-text help-text-select">
                        Billing provider is required
                      </span>
                    </Conditional>
                  </FieldWrapper>

                  <FieldWrapper
                    key="departmentPerformingInfo"
                    label="Department"
                    type="department-performing-info"
                    required={invoiceOptions?.required_fields?.includes(
                      RequiredField.DEPARTMENT
                    )}>
                    <div className="department-select-box">
                      {this.props.invoice.department.code ? (
                        <div className="cmx-chip cmx-chip-entity">
                          {this.props.invoice.department.name
                            ? this.props.invoice.department.name
                            : this.props.invoice.department.code}
                          <button
                            type="button"
                            onClick={this.departmentRemove}
                            className="cmx-chip-entity-remove btn-remover"
                            aria-label="Remove department">
                            <BiomeIcon
                              iconName={IconName.Cancel}
                              iconSize="16"
                              iconClass="cmx-chip-entity-remove__icon"
                            />
                          </button>
                        </div>
                      ) : (
                        <Lookup
                          {...lookupArgs}
                          minLength={1}
                          onSearchSelection={this.departmentChange}
                          FieldDefinitions={[]}
                          resultFormatter={resultFormatterDept}
                          onSearch={this.handleDepartmentSearch}
                          searchResults={allDepartmentOptions
                            .map((departmentOption) => {
                              return {
                                code: departmentOption.value,
                                codeType: departmentOption.value,
                                description: departmentOption.label
                                  ? departmentOption.label
                                  : departmentOption.value,
                                shortDescription: departmentOption.label
                                  ? departmentOption.label
                                  : departmentOption.value,
                                tenantDefined: null,
                                effectiveDate: ''
                              };
                            })
                            .filter((department) =>
                              departmentSearch(
                                department,
                                this.state.departmentSearchText
                              )
                            )}
                          actionPrefix={''}
                          autoSearch={true}
                          placeholderText={'Choose a department'}
                        />
                      )}
                    </div>
                    <Conditional
                      every={[
                        !this.checkRequiredFieldValid(RequiredField.DEPARTMENT)
                      ]}>
                      <span className="error-text help-text help-text-select">
                        Department is required
                      </span>
                    </Conditional>
                  </FieldWrapper>

                  {feeSchedules.length > 1 && (
                    <FieldWrapper
                      key="feeSchedulePerformingInfo"
                      label="Bill Area"
                      type="feeSchedule-performing-info"
                      required={invoiceOptions?.required_fields?.includes(
                        RequiredField.BILLING_AREA
                      )}>
                      <div className="feeSchedule-select-box">
                        <SelectList
                          placeholder="Choose a Billing area"
                          name="feeSchedule"
                          options={feeSchedules}
                          value={this.props.invoice.fee_schedule}
                          onChange={this.feeScheduleChange}
                        />
                        <label className="feeSchedule-select-label">
                          <Icon
                            svgIconName="BiomeChevronDownSVG20"
                            svgIconSize="16"
                            svgIconClass="select-checvron"
                          />
                        </label>
                      </div>
                      <Conditional
                        every={[
                          this.checkRequiredFieldValid(
                            RequiredField.BILLING_AREA
                          )
                        ]}>
                        <span className="help-text help-text-select">
                          The billing area where the service was performed.
                        </span>
                      </Conditional>
                      <Conditional
                        every={[
                          !this.checkRequiredFieldValid(
                            RequiredField.BILLING_AREA
                          )
                        ]}>
                        <span className="error-text help-text help-text-select">
                          Billing area is required
                        </span>
                      </Conditional>
                    </FieldWrapper>
                  )}

                  <div style={{ display: 'none' }}>
                    <FieldWrapper
                      key="billingArea"
                      label="Area"
                      required={true}>
                      <SelectList
                        name="billingArea"
                        options={billingAreaOptions}
                        value=""
                      />
                      <span className="help-text help-text-select">
                        The billing area where the service was performed.
                      </span>
                    </FieldWrapper>
                  </div>
                </FieldSetWrapper>

                <FieldSetWrapper legend="Other Providers">
                  <FieldWrapper
                    key="performingInformationProvider"
                    label="Performing"
                    type="performing-information-provider"
                    required={invoiceOptions?.required_fields?.includes(
                      RequiredField.PERFORMING_PROVIDER
                    )}>
                    {hasPerformer ? (
                      <div className="cmx-chip cmx-chip-entity">
                        {invoice.performing_provider_name}
                        <button
                          type="button"
                          onClick={this.handlePerformerRemove}
                          className="cmx-chip-entity-remove btn-remover"
                          aria-label="Remove provider">
                          <BiomeIcon
                            iconName={IconName.Cancel}
                            iconSize="16"
                            iconClass="cmx-chip-entity-remove__icon"
                          />
                        </button>
                      </div>
                    ) : (
                      <Lookup
                        {...lookupArgs}
                        onSearchSelection={this.handleOnPerformerAdditionAdd}
                        onSearch={this.handlePerformerSearch}
                        searchResults={
                          this.props.performingProviderSearchResults
                        }
                        actionPrefix={''}
                        autoSearch={true}
                        placeholderText={
                          isIPCServiceLine
                            ? 'Search for a performer by name'
                            : 'Search for a performer'
                        }
                      />
                    )}

                    <Conditional
                      every={[
                        this.checkRequiredFieldValid(
                          RequiredField.PERFORMING_PROVIDER
                        )
                      ]}>
                      <span className="help-text help-text-select">
                        The provider who performed the services.
                      </span>
                    </Conditional>
                    <Conditional
                      every={[
                        !this.checkRequiredFieldValid(
                          RequiredField.PERFORMING_PROVIDER
                        )
                      ]}>
                      <span className="error-text help-text help-text-select">
                        Performing provider is required
                      </span>
                    </Conditional>
                  </FieldWrapper>
                  <FieldWrapper
                    key="referringInformationProvider"
                    label="Referring"
                    type="performing-information-referring-provider"
                    required={invoiceOptions?.required_fields?.includes(
                      RequiredField.REFERRING_PROVIDER
                    )}>
                    {hasReferringPerformer ? (
                      <div className="cmx-chip cmx-chip-entity">
                        {invoice.referring_provider_name}
                        <button
                          type="button"
                          onClick={this.handleReferringPerformerRemove}
                          className="cmx-chip-entity-remove btn-remover"
                          aria-label="Remove referring provider">
                          <BiomeIcon
                            iconName={IconName.Cancel}
                            iconSize="16"
                            iconClass="cmx-chip-entity-remove__icon"
                          />
                        </button>
                      </div>
                    ) : (
                      <Lookup
                        {...lookupArgs}
                        onSearchSelection={
                          this.handleOnReferrerPerformerAdditionAdd as any
                        }
                        onSearch={this.handleReferrerSearch}
                        searchResults={
                          this.props.referringProviderSearchResults
                        }
                        actionPrefix={''}
                        autoSearch={true}
                        placeholderText={
                          isIPCServiceLine
                            ? 'Search for a referring provider by name'
                            : 'Search for a referring provider'
                        }
                      />
                    )}

                    <Conditional
                      every={[
                        this.checkRequiredFieldValid(
                          RequiredField.REFERRING_PROVIDER
                        )
                      ]}>
                      <span className="help-text help-text-select">
                        The provider who referred the service.
                      </span>
                    </Conditional>
                    <Conditional
                      every={[
                        !this.checkRequiredFieldValid(
                          RequiredField.REFERRING_PROVIDER
                        )
                      ]}>
                      <span className="error-text help-text help-text-select">
                        Referring provider is required
                      </span>
                    </Conditional>
                  </FieldWrapper>
                </FieldSetWrapper>

                <button
                  key="Save"
                  type="button"   
                  onClick={this.handleFormSubmit}  
                  disabled={this.state?.disableSubmit ?? true}
                  className={`chakra-primary-button submit-btn invoice-submit ${
                    this.state?.disableSubmit ? 'disabled' : ''
                  }`}>
                  Save
                </button>
              </form>
            </Form>
          </div>
        </Modal>
      </div>
    );
  }
}

export { InvoiceEditor };
